import React, { FC } from "react";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { Account } from "../../types";
import Typography from "../typography/Typography";
import { Button } from "../button";
import Stack from "../stack/Stack";
import { useBackgroundColor } from "../../utils";
import { ThemeColorsKeys } from "../../styles/tokens/colors";
import Image from "../image/Image";
import Details from "../details/Details";
import Link from "../link/Link";
import Divider from "../divider/Divider";
import AccountFees from "../accountFees/AccountFees";
import { ThemeButtonKeys } from "../../styles/tokens/buttons";

// Interface

export interface StyledHeaderProps {
  backgroundColor: ThemeColorsKeys;
}

// Styles

const StyledFeaturedAccount = styled.div`
  ${({ theme: { space, colors, media, borders, borderRadii } }) => css`
    ${useBackgroundColor("white")}
    border: 1px solid ${colors.darkBlue};
    border-radius: ${borderRadii.small};
    overflow: hidden;
    isolation: isolate;
  `};
`;

const StyledFeaturedImage = styled.div`
  ${({
    theme: { space, colors, media, borders, borderRadii, maxMedia },
  }) => css`
    ${useBackgroundColor("lilac")}
    border-bottom: 1px solid ${colors.darkBlue};
    min-height: 304px;
    position: relative;

    @media (min-width: ${media.medium}) {
      min-height: 404px;
    }

    @media (min-width: ${media.large}) {
      min-height: 204px;
    }
  `};
`;

const StyledBody = styled.div`
  ${({ theme: { space, colors, media, borders, borderRadii } }) => css`
    padding-left: ${space.fluidSmallToLarge};
    padding-right: ${space.fluidSmallToLarge};
    padding-bottom: ${space.fluidSmallToLarge};
    z-index: 9;
    position: relative;

    @media (min-width: ${media.large}) {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: ${space.large};
    }
  `};
`;

const StyledLeftColumn = styled.div`
  ${({ theme: { space, colors, media, borders, borderRadii, maxMedia } }) =>
    css``};
`;

const StyledLeftColumnBody = styled.div`
  ${({
    theme: { space, colors, media, borders, borderRadii, maxMedia },
  }) => css`
    border-right: 1px solid ${colors.darkBlue};
    border-bottom: 1px solid ${colors.darkBlue};
    border-left: 1px solid ${colors.darkBlue};
    border-bottom-left-radius: ${borderRadii.small};
    border-bottom-right-radius: ${borderRadii.small};
    overflow: hidden;
  `};
`;

const StyledRightColumn = styled.div`
  ${({ theme: { space, colors, media, borders, borderRadii } }) => css`
    padding-top: ${space.medium};
    /* 
    @media (min-width: ${media.large}) {
      padding-top: 276px;
    } */
  `};
`;

const StyledHeader = styled.div`
  ${({ theme: { space, colors, media, borderRadii } }) => css`
    padding-left: ${space.fluidSmallToLarge};
    padding-right: ${space.fluidSmallToLarge};
    position: absolute;
    bottom: 0;
    height: auto;
    width: 100%;
    display: grid;
    @media (min-width: ${media.large}) {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: ${space.large};
    }
  `};
`;

const StyledHeaderContainer = styled(Stack)<StyledHeaderProps>`
  ${({ theme: { space, colors, media, borderRadii }, backgroundColor }) => css`
    ${useBackgroundColor(backgroundColor)}
    padding: ${space.fluidSmallToLarge};
    height: auto;
    border-left: solid 1px currentColor;
    border-right: solid 1px currentColor;
    border-top: solid 1px currentColor;
    border-top-left-radius: ${borderRadii.small};
    border-top-right-radius: ${borderRadii.small};
  `};
`;

const StyledBenefits = styled(Stack)`
  ${({ theme: { space, colors, media, borders, borderRadii } }) => css`
    padding: ${space.fluidSmallToMedium};
    ${useBackgroundColor("neutral")}
  `};
`;

const StyledDetails = styled(Stack)`
  ${({
    theme: { space, colors, media, borders, borderRadii, maxMedia },
  }) => css`
    ${useBackgroundColor("white")}
  `};
`;

const StyledActions = styled.div`
  ${({
    theme: { space, colors, media, borders, borderRadii, maxMedia },
  }) => css`
    padding-top: ${space.fluidMediumToLarge};
    padding-bottom: ${space.fluidMediumToLarge};
    padding-left: ${space.fluidSmallToLarge};
    padding-right: ${space.fluidSmallToLarge};
    display: flex;
    gap: ${space.fluidXSmallToSmall};
    border-top: solid 1px currentColor;

    a {
      &:last-of-type {
        padding: 0;
        flex-grow: 1;
      }
    }

    @media (max-width: ${maxMedia.large}) {
      flex-direction: column;
    }
  `};
`;

const StyledDesktopImage = styled(Image)`
  ${({
    theme: { space, colors, media, borders, borderRadii, maxMedia },
  }) => css`
    object-fit: cover;
    height: 100%;
    *,
    img {
      object-fit: cover;
      height: 100%;
    }
    @media (max-width: ${maxMedia.large}) {
      display: none;
    }
  `};
`;

const StyledMobileImage = styled(Image)`
  ${({
    theme: { space, colors, media, borders, borderRadii, maxMedia },
  }) => css`
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    object-fit: cover;
    height: 100%;
    *,
    img {
      object-fit: cover;
      height: 100%;
    }

    @media (min-width: ${media.large}) {
      display: none;
    }
  `};
`;

const StyledCost = styled(Typography)`
  ${({ theme: { space, colors, media, borderRadii } }) => css`
   @media (min-width: 600px) {
      display: flex;
      align-items: baseline;
      gap: ${space.xSmall};
      flex-wrap: wrap;
    }
  `};
`;

// JSX

export const FeaturedAccount: FC<Account> = ({
  name,
  subHeading,
  secondarySubheading,
  monthlyCost,
  details,
  benefits,
  primaryButton,
  secondaryLink,
  allFeesLink,
  colour = "zemplerBlue",
  featuredImage,
  mobileImage,
  accountFeeHeading,
  accountFeeSubHeading,
  limits,
  fees,
  feeSmallPrint,
  buttonColour,
  hideArrowFees,
  hideArrowPrimary,
  hideArrowSecondary,
  
}) => {
  function makeFirstCharLowercase(str: string): string {
    if (str.length === 0) return str; // Return empty string if input is empty

    // Convert first character to lowercase and concatenate it with the rest of the string
    return str.charAt(0).toLowerCase() + str.slice(1);
  }

  return (
    <StyledFeaturedAccount>
      <StyledFeaturedImage>
        <StyledDesktopImage src={featuredImage} alt={name} />
        <StyledMobileImage src={mobileImage} alt={name} />
        <StyledHeader>
          <StyledHeaderContainer
            gap="fluidXxSmallToxSmall"
            backgroundColor={colour}
          >
            <Typography component="h2" variant="headingMedium">
              {name}
            </Typography>
            <Typography component="p" variant="bodyMediumToLarge">
              {subHeading} 
            </Typography>
            <StyledCost
              component="p"
              variant="secondaryHeadingSmallToMediumBold"
            >
              {monthlyCost} {secondarySubheading && <Typography variant="body" component="span">{secondarySubheading}</Typography>}
            </StyledCost>
          </StyledHeaderContainer>
        </StyledHeader>
      </StyledFeaturedImage>
      <StyledBody>
        <StyledLeftColumn>
          <StyledLeftColumnBody>
            {benefits && (
              <StyledBenefits gap="fluidXSmallToSmall">
                <Details items={benefits} />
              </StyledBenefits>
            )}
          </StyledLeftColumnBody>
        </StyledLeftColumn>
        <StyledRightColumn>
          <StyledDetails gap="fluidXSmallToSmall">
            <Details items={details} />
            <Divider />
            <AccountFees
              heading={accountFeeHeading}
              subHeading={accountFeeSubHeading}
              fees={fees}
              limits={limits}
              accountName={name}
              feeSmallPrint={feeSmallPrint}
              allFeesLink={allFeesLink}
              hideArrow={hideArrowFees}
            />
          </StyledDetails>
        </StyledRightColumn>
      </StyledBody>

      {primaryButton || secondaryLink ? (
        <StyledActions>
          {primaryButton && (
            <Button
              variant={
                (makeFirstCharLowercase(
                  buttonColour || ""
                ) as ThemeButtonKeys) || "zemplerBlue"
              }
              arrowRight={!hideArrowPrimary}
              {...primaryButton}
              dataLayerClickContext="Featured Account - primary button"
            />
          )}

          {secondaryLink && (
            <Link
              {...secondaryLink}
              dataLayerClickContext="Featured Account - secondary link"
              hideArrow={hideArrowSecondary}
            />
          )}
        </StyledActions>
      ) : (
        ""
      )}
    </StyledFeaturedAccount>
  );
};

export default FeaturedAccount;
